import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTourStore } from "@/store/tour";
import { getTourSteps } from "./tourDefinitions";
import { TourOverlay } from "./TourOverlay";
import { TourTooltip } from "./TourTooltip";
import { TourChallenge } from "./TourChallenge";

function ResumeDialog() {
  const { t } = useTranslation();
  const { showResumeDialog, resumeTourName, acceptResume, dismissResume, setDontShowAgain } = useTourStore();
  const [dontShow, setDontShow] = useState(false);

  if (!showResumeDialog) return null;

  const handleDismiss = () => {
    if (dontShow && resumeTourName) {
      setDontShowAgain(resumeTourName, true);
    }
    dismissResume();
    setDontShow(false);
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 mx-4">
        <h3 className="text-lg font-semibold mb-2">{t("tour.resumeTitle")}</h3>
        <p className="text-sm text-gray-600 mb-4">{t("tour.resumeMessage")}</p>
        <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
          />
          {t("tour.dontShowAgain")}
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={handleDismiss}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t("tour.resumeNo")}
          </button>
          <button
            onClick={() => acceptResume(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t("tour.restartTour")}
          </button>
          <button
            onClick={() => acceptResume(true)}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700"
          >
            {t("tour.resumeYes")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TourProvider() {
  const {
    activeTour,
    currentStep,
    isRunning,
    helpers,
    dontShowAgain,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    setDontShowAgain,
  } = useTourStore();

  const steps = activeTour ? getTourSteps(activeTour) : [];
  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  // Execute beforeAction when step changes
  useEffect(() => {
    if (!step?.beforeAction || !isRunning) return;
    const helper = helpers[step.beforeAction];
    if (helper) {
      try { helper(); } catch { /* ignore */ }
    }
  }, [step, isRunning, helpers]);

  // Handle next — complete tour if at last step
  const handleNext = useCallback(() => {
    if (isLast) {
      completeTour();
    } else {
      nextStep();
    }
  }, [isLast, completeTour, nextStep]);

  // Handle skip — if don't-show-again is checked, persist it
  const handleSkip = useCallback(() => {
    if (activeTour && dontShowAgain[activeTour]) {
      setDontShowAgain(activeTour, true);
    }
    if (isLast) {
      completeTour();
    } else {
      skipTour();
    }
  }, [activeTour, dontShowAgain, setDontShowAgain, isLast, completeTour, skipTour]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isRunning) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleSkip();
      } else if (e.key === "ArrowRight" && !step?.challengeMode) {
        handleNext();
      } else if (e.key === "ArrowLeft") {
        prevStep();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isRunning, handleSkip, handleNext, prevStep, step?.challengeMode]);

  // Auto-skip if element not found after timeout
  const handleTargetNotFound = useCallback(() => {
    if (isLast) {
      completeTour();
    } else {
      nextStep();
    }
  }, [isLast, completeTour, nextStep]);

  // Handle dontShowAgain toggle
  const handleDontShowAgainChange = useCallback(
    (value: boolean) => {
      if (activeTour) setDontShowAgain(activeTour, value);
    },
    [activeTour, setDontShowAgain],
  );

  return (
    <>
      {/* Resume dialog — always rendered at root level */}
      <ResumeDialog />

      {/* Active tour overlay + tooltip */}
      {isRunning && activeTour && step && (
        <>
          <TourOverlay
            target={step.target}
            padding={step.highlightPadding}
            onTargetNotFound={handleTargetNotFound}
          />
          <TourTooltip
            target={step.target}
            title={step.title}
            content={step.content}
            placement={step.placement}
            currentStep={currentStep}
            totalSteps={steps.length}
            onNext={handleNext}
            onPrev={prevStep}
            onSkip={handleSkip}
            isFirstStep={isFirst}
            isLastStep={isLast}
            showDontShowAgain
            dontShowAgain={activeTour ? (dontShowAgain[activeTour] ?? false) : false}
            onDontShowAgainChange={handleDontShowAgainChange}
            padding={step.highlightPadding}
            challengeSlot={
              step.challengeMode && step.challengeCheck ? (
                <TourChallenge
                  challengeCheck={step.challengeCheck}
                  onComplete={handleNext}
                />
              ) : undefined
            }
          />
        </>
      )}
    </>
  );
}
